import { useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { Divider, Header, Label, List, Message, Segment, Tab } from 'semantic-ui-react';
import ArchivedReviewChanges from '../../../components/admin/updates/components/archivedReviewChanges';
import AppliedUpdateChanges from '../../../components/admin/updates/components/appliedUpdateChanges';
import Loader from '../../../components/loader';
import RouteC from '../../../constnats/routeConstants';
import { UPDATE_PHASE } from '../../../constnats/tableUpdate';
import { appliedUpdateSchema, type AppliedUpdateType } from '../../../schemas/appliedUpdate.schemas';
import { getUpdateWithReviewsAndOwners } from '../../../service/tableUpdates';
import { serverToLocalDate } from '../../../utils/dateTimeUtils';
import { PathFactory } from '../../../utils/repository/utils';
import { canViewFullReview } from '../../../utils/reviewAccess';
import './appliedUpdate.css';

function AppliedUpdate() {
    const { companyId, updateId } = useParams<'companyId' | 'updateId'>();
    const [update, setUpdate] = useState<AppliedUpdateType | null>(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        let active = true;
        setUpdate(null);
        setFailed(false);
        if (!updateId) {
            setFailed(true);
            return;
        }

        getUpdateWithReviewsAndOwners(updateId)
            .then((response) => appliedUpdateSchema.parse(response))
            .then((response) => {
                if (active) {
                    setUpdate(response);
                }
            }, () => {
                if (active) {
                    setFailed(true);
                }
            });

        return () => { active = false; };
    }, [updateId]);

    if (failed) {
        return <Message error>Unable to load applied update.</Message>;
    }
    if (!update) {
        return <Loader />;
    }
    if (String(update.companyId) !== companyId) {
        return <Message error>Update not found.</Message>;
    }
    if (update.phase !== UPDATE_PHASE.APPLIED) {
        return <Navigate replace to={PathFactory.create([
            RouteC.EDIT_COMPANY_CONFIG_INTERNAL, companyId, 'update', update.id,
        ])} />;
    }

    const updatesTabUrl = PathFactory.create([
        RouteC.EDIT_COMPANY_CONFIG_INTERNAL, companyId, 'updates', update.srcSchemaId,
    ]);
    const panes = [
        {
            menuItem: { key: 'update-changes', content: 'Update Changes' },
            render: () => (
                <Tab.Pane>
                    <AppliedUpdateChanges key={update.id} updateId={update.id} />
                </Tab.Pane>
            ),
        },
        ...update.reviews.map((review) => ({
            menuItem: { key: review.id, content: `${review.owner?.fullName ?? review.ownerId} — ${review.type}` },
            render: () => (
                <Tab.Pane>
                    <ArchivedReviewChanges
                        key={review.id}
                        reviewId={review.id}
                        notes={review.notes}
                        restricted={!canViewFullReview(review, update.phase, update.ownerId)}
                    />
                </Tab.Pane>
            ),
        })),
    ];

    return (
        <div className="applied-update">
            <Header as="h2">
                <Link to={updatesTabUrl}>{update.summary}</Link>
                {' — Update '}{update.id}
            </Header>
            <Segment>
                <List horizontal divided>
                    <List.Item>
                        <List.Content>
                            <List.Header>Owner</List.Header>
                            {update.owner?.disabled ? (
                                <Label color="red" size="mini">{update.owner.fullName}</Label>
                            ) : update.owner?.fullName ?? '—'}
                        </List.Content>
                    </List.Item>
                    <List.Item>
                        <List.Content>
                            <List.Header>Phase</List.Header>
                            {update.phase}
                        </List.Content>
                    </List.Item>
                    <List.Item>
                        <List.Content>
                            <List.Header>Purpose</List.Header>
                            {update.purpose}
                        </List.Content>
                    </List.Item>
                    <List.Item>
                        <List.Content>
                            <List.Header>Created On</List.Header>
                            {serverToLocalDate(update.createdOn)}
                        </List.Content>
                    </List.Item>
                    <List.Item>
                        <List.Content>
                            <List.Header>Updated On</List.Header>
                            {serverToLocalDate(update.updatedOn)}
                        </List.Content>
                    </List.Item>
                </List>
                <Divider />
                <Header as="h5">Description</Header>
                <div className="applied-update__description">{update.description}</div>
            </Segment>
            <Tab key={update.id} panes={panes} defaultActiveIndex={0} />
        </div>
    );
}

export default AppliedUpdate;
